// Presentation formatting via Intl. Nulls render as an em dash, never as 0.
export const DASH = '—';

/**
 * A verified badge's label. PRIVACY: only ever the domain (never the verified email address) plus
 * the org name when one is known — see packages/shared VerifiedBadgeSchema.
 */
export function formatVerifiedBadge(b: { kind: 'work_email' | 'domain'; domain: string; orgName: string | null }): string {
  const base = b.kind === 'work_email' ? `Verified work email · ${b.domain}` : `Verified domain · ${b.domain}`;
  return b.orgName ? `${base} (${b.orgName})` : base;
}

export function formatNullable(v: number | string | null | undefined): string {
  return v === null || v === undefined ? DASH : String(v);
}

export function formatNumber(v: number | null | undefined, locale?: string): string {
  if (v === null || v === undefined) return DASH;
  return new Intl.NumberFormat(locale).format(v);
}

export function formatCents(cents: number | null | undefined, currency = 'USD', locale?: string) {
  if (cents === null || cents === undefined) return DASH;
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86400_000],
  ['month', 30 * 86400_000],
  ['day', 86400_000],
  ['hour', 3600_000],
  ['minute', 60_000],
  ['second', 1000],
];

export function formatRelative(ts: number | null | undefined, now = Date.now(), locale?: string) {
  if (ts === null || ts === undefined) return 'never';
  const diff = ts - now;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms || unit === 'second') return rtf.format(Math.round(diff / ms), unit);
  }
  return rtf.format(0, 'second');
}

export function formatDate(ts: number | null | undefined, locale?: string) {
  if (ts === null || ts === undefined) return DASH;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(ts);
}

/** Short chart tick label ("Sep 4") for ISO day strings; other labels pass through (D09). */
export function formatAxisDate(label: string, locale?: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
  if (!m) return label;
  const d = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
}

/** True when a series has nothing to show (every point 0 or null): render an empty state, not a flat line. */
export function isEmptySeries(points: readonly { y: number | null }[]): boolean {
  return points.every((p) => p.y === null || p.y === 0);
}

const WORK_MODE: Record<string, string> = { remote: 'Remote', hybrid: 'Hybrid', onsite: 'On-site' };

/** Location + work mode without repeating ourselves ("Remote Remote", D23). */
export function jobPlace(location: string | null | undefined, workMode: string | null | undefined): string[] {
  const loc = (location ?? '').trim();
  const mode = workMode ? (WORK_MODE[workMode] ?? workMode) : '';
  const out: string[] = [];
  if (loc) out.push(loc);
  if (mode && !loc.toLowerCase().includes(mode.toLowerCase())) out.push(mode);
  return out;
}
